import { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { getUserId } from "modules/CommonSelectors";

const LOCAL_EVENT_PREFIX = "shared_local_storage_";

const useSharedLocalStorage = (key, defaultValue) => {
  const userId = getUserId();
  const userSpecificKey = userId ? `${key}_${userId}` : key;

  const getInitialValue = () => {
    const stored = localStorage.getItem(userSpecificKey);
    if (!stored || stored === "undefined") {
      return _.cloneDeep(defaultValue);
    }

    try {
      return JSON.parse(stored);
    } catch (e) {
      return _.cloneDeep(defaultValue);
    }
  };

  const [value, setValue] = useState(getInitialValue);

  const updateValue = useCallback(
    newValueOrUpdater => {
      const resolvedValue = _.isFunction(newValueOrUpdater)
        ? newValueOrUpdater(value)
        : newValueOrUpdater;

      if (!_.isEqual(resolvedValue, value)) {
        localStorage.setItem(userSpecificKey, JSON.stringify(resolvedValue));
        setValue(_.cloneDeep(resolvedValue));
        window.dispatchEvent(
          new CustomEvent(`${LOCAL_EVENT_PREFIX}${userSpecificKey}`, {
            detail: resolvedValue,
          })
        );
      }
    },
    [value, userSpecificKey]
  );

  useEffect(() => {
    const handleCustomEvent = e => {
      const newValue = e.detail;
      if (!_.isEqual(newValue, value)) {
        setValue(_.cloneDeep(newValue));
      }
    };

    window.addEventListener(
      `${LOCAL_EVENT_PREFIX}${userSpecificKey}`,
      handleCustomEvent
    );
    return () => {
      window.removeEventListener(
        `${LOCAL_EVENT_PREFIX}${userSpecificKey}`,
        handleCustomEvent
      );
    };
  }, [userSpecificKey, value]);

  return [value, updateValue];
};

export default useSharedLocalStorage;
